import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./LoginModern.css";
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import BookExpert from '../Explore/BookExpert';
import LogUp from '../LogUp/LogUp';
import { authService } from '../../services';

// Read Google client id from environment or global (dev-friendly)
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || window.__GOOGLE_CLIENT_ID__ || '';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isBookExpertVisible, setIsBookExpertVisible] = useState(false);
  const [isLoginVisible, setIsLoginVisible] = useState(false);
  const [isSignUpVisible, setIsSignUpVisible] = useState(false);
  const [loginFormData, setLoginFormData] = useState({
    username: '',
    password: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [googleSdkLoaded, setGoogleSdkLoaded] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Auto-fill form data from registration
  useEffect(() => {
    if (location.state?.prefilledData) {
      const { username, email } = location.state.prefilledData;
      console.log('Auto-filling login form with:', { username, email });
      
      setLoginFormData(prev => ({
        ...prev,
        username: username || ''
      }));
      
      // Show success message for auto-fill
      if (username) {
        alert(`Chào mừng ${username}! Vui lòng nhập mật khẩu để đăng nhập.`);
      }
    }
  }, [location.state]);

  // Handle input changes
  const handleLoginInputChange = (e) => {
    const { name, value } = e.target;
    setLoginFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateLoginForm = () => {
    const newErrors = {};
    
    if (!loginFormData.username) {
      newErrors.username = 'Username is required';
    } else if (loginFormData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }
    
    if (!loginFormData.password) {
      newErrors.password = 'Password is required';
    } else if (loginFormData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateLoginForm()) {
      return;
    }
    
    setIsLoginLoading(true);
    
    try {
      // Call API để đăng nhập
      const response = await authService.login(loginFormData.username, loginFormData.password);
      
      console.log('Login successful:', response);
      
      // Attempt to fetch profile after login (if backend provides it)
      try {
        const profile = await authService.getProfile();
        console.log('Fetched profile after login:', profile);
      } catch (pErr) {
        console.warn('Could not fetch profile after login:', pErr);
      }

      // Show success modal for 3 seconds
      setShowSuccessModal(true);
      
      // Auto hide modal and redirect after 3 seconds
      setTimeout(() => {
        setShowSuccessModal(false);
        // Ensure scroll to top before navigation
        window.scrollTo(0, 0);
        // Redirect to explore page after successful login
        navigate('/explore');
      }, 3000);
      
    } catch (error) {
      console.error('Login error:', error);
      
      // Xử lý lỗi từ API
      let errorMessage = 'Đăng nhập thất bại. Vui lòng thử lại.';
      
      // Extract error message from different error formats
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.status === 401) {
        errorMessage = 'Username hoặc mật khẩu không đúng.';
      } else if (error.response?.status === 400) {
        // Try to get the actual error message from backend
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (typeof error.response?.data === 'string') {
          errorMessage = error.response.data;
        } else {
          errorMessage = 'Dữ liệu đăng nhập không hợp lệ. Vui lòng kiểm tra lại thông tin.';
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      console.error('Displaying error message to user:', errorMessage);
      alert(errorMessage);
    } finally {
      setIsLoginLoading(false);
    }
  };

  // Handle "Can't log in" link click
  const handleCantLogin = () => {
    // Scroll to top before navigation
    window.scrollTo(0, 0);
    // Navigate to sign up page
    navigate('/signup');
  };

  // Handle Google login (tries to use a provided Google token or prompts for one)
  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    try {
      // Try to obtain a token from a global test variable (useful for dev)
      let googleToken = window.__TEST_GOOGLE_TOKEN__ || null;

      // If no token available, ask developer/tester to paste one (safe fallback)
      if (!googleToken) {
        googleToken = window.prompt('Paste Google ID token (for testing)');
      }

      if (!googleToken) {
        alert('No Google token provided. Aborting Google login.');
        return;
      }

      // Call the service which posts to /Account/GoogleLogin
      const result = await authService.googleLogin(googleToken);
      console.log('Google login result:', result);

      // After successful login, try to fetch profile
      try {
        const profile = await authService.getProfile();
        console.log('Fetched profile after Google login:', profile);
      } catch (pErr) {
        console.warn('Could not fetch profile after Google login:', pErr);
      }

      alert('Đăng nhập bằng Google thành công!');
      window.scrollTo(0, 0);
      navigate('/explore');
    } catch (error) {
      console.error('Google login error:', error);
      let msg = 'Đăng nhập bằng Google thất bại. Vui lòng thử lại.';
      if (error?.response?.data?.message) msg = error.response.data.message;
      else if (typeof error === 'string') msg = error;
      alert(msg);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  // Initialize Google Identity Services if client id provided
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    // If script already present, try to initialize
    const existing = document.getElementById('google-identity-script');
    if (existing) {
      tryInitialize();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.id = 'google-identity-script';
    script.async = true;
    script.defer = true;
    script.onload = tryInitialize;
    script.onerror = () => {
      console.warn('Failed to load Google Identity Services script');
    };
    document.body.appendChild(script);

    function tryInitialize() {
      if (!window.google || !window.google.accounts || !window.google.accounts.id) {
        console.warn('Google Identity API not available after script load');
        return;
      }

      // Initialize the one-tap / button
      try {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: async (response) => {
            // response.credential is the ID token
            if (!response?.credential) return;
            setIsGoogleLoading(true);
            try {
              await authService.googleLogin(response.credential);
              try {
                await authService.getProfile();
              } catch (pErr) {
                console.warn('Profile fetch failed after Google login:', pErr);
              }
              alert('Đăng nhập bằng Google thành công!');
              window.scrollTo(0, 0);
              navigate('/explore');
            } catch (err) {
              console.error('Google login via SDK failed:', err);
              alert(err?.response?.data?.message || err?.message || 'Google login failed');
            } finally {
              setIsGoogleLoading(false);
            }
          }
        });

        // Render button into container
        try {
          const container = document.getElementById('gsi-button-container');
          if (container) {
            // Clear any existing content
            container.innerHTML = '';
            window.google.accounts.id.renderButton(container, {
              theme: 'outline',
              size: 'large'
            });
          }
        } catch (renderErr) {
          console.warn('Error rendering Google button:', renderErr);
        }

        setGoogleSdkLoaded(true);
      } catch (e) {
        console.warn('Error initializing Google Identity Services:', e);
      }
    }

    // cleanup
    return () => {
      // no-op; keep script to reuse across navigation
    };
  }, [navigate]);

  // If user clicked Organizations, show BookExpert
  if (isBookExpertVisible) {
    return <BookExpert />;
  }

  // If user clicked Create Account, show LogUp
  if (isSignUpVisible) {
    return <LogUp onBackToLogin={() => setIsSignUpVisible(false)} />;
  }

  // If user clicked Login while already on Login page, do nothing (prevent infinite loop)
  // isLoginVisible state is handled by parent components

  return (
    <div className="login-modern-container page-with-header">
      <Header 
        showBookExpert={isBookExpertVisible} 
        setShowBookExpert={setIsBookExpertVisible}
        showLogin={isLoginVisible}
        setShowLogin={setIsLoginVisible}
      />

      {/* Main Content */}
      <main className="login-modern-main-content">
        <div className="login-modern-content-wrapper">
          {/* Logo and Title Section */}
          <div className="login-modern-logo-section">
            <div className="login-modern-logo-circle">
              <svg width="40" height="40" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="20" fill="white" opacity="0.9"/>
                <path d="M24 12L28 20H32L26 26L28 34L24 30L20 34L22 26L16 20H20L24 12Z" fill="#667eea"/>
              </svg>
            </div>
            <h1 className="login-modern-main-title">Chào mừng trở lại</h1>
            <p className="login-modern-subtitle">Đăng nhập vào tài khoản của bạn để tiếp tục</p>
          </div>

          {/* Login Form Container */}
          <div className="login-modern-form-container">
            {/* Email Login Section */}
            <div className="login-modern-email-section">
              <form className="login-modern-form-content" onSubmit={handleLoginSubmit}>
                <h2 className="login-modern-form-title">Đăng nhập</h2>
                <p className="login-modern-form-subtitle">Nhập thông tin đăng nhập để truy cập tài khoản của bạn</p>
                
                {/* Username Field */}
                <div className="login-modern-input-group">
                  <label className="login-modern-input-label">Tên đăng nhập</label>
                  <input 
                    type="text" 
                    name="username"
                    value={loginFormData.username}
                    onChange={handleLoginInputChange}
                    className={`login-modern-input-field ${validationErrors.username ? 'input-error' : ''}`}
                    placeholder="Nhập tên đăng nhập của bạn"
                  />
                  {validationErrors.username && <span className="login-modern-error-message">{validationErrors.username}</span>}
                </div>

                {/* Password Field */}
                <div className="login-modern-input-group">
                  <div className="login-modern-password-header">
                    <label className="login-modern-input-label">Mật khẩu</label>
                    <button 
                      className="login-modern-password-toggle"
                      type="button"
                      onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                    >
                      {isPasswordVisible ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M3 3L21 21M10.584 10.587C10.208 11.037 10 11.59 10 12.2C10 13.7 11.3 15 12.8 15C13.41 15 13.96 14.79 14.41 14.42M9.363 5.365C10.105 4.611 11.001 4.2 12 4.2C14.8 4.2 17 7.2 17 12.2C17 13.2 16.8 14.1 16.4 14.9M12 4.2C9.2 4.2 7 7.2 7 12.2C7 13.2 7.2 14.1 7.6 14.9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                      )}
                      <span className="login-modern-password-toggle-text">
                        {isPasswordVisible ? "Ẩn" : "Hiện"}
                      </span>
                    </button>
                  </div>
                  <input 
                    type={isPasswordVisible ? "text" : "password"} 
                    name="password"
                    value={loginFormData.password}
                    onChange={handleLoginInputChange}
                    className={`login-modern-input-field ${validationErrors.password ? 'input-error' : ''}`}
                    placeholder="Nhập mật khẩu của bạn"
                  />
                  {validationErrors.password && <span className="login-modern-error-message">{validationErrors.password}</span>}
                </div>

                {/* Login Button */}
                <button 
                  type="submit" 
                  className="login-modern-login-btn"
                  disabled={isLoginLoading}
                >
                  {isLoginLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Đang đăng nhập...
                    </>
                  ) : (
                    'Đăng nhập'
                  )}
                </button>
              </form>
            </div>

            {/* Vertical Divider */}
            <div className="login-modern-vertical-divider">
              <div className="login-modern-divider-line"></div>
              <span className="login-modern-divider-text">OR</span>
              <div className="login-modern-divider-line"></div>
            </div>

            {/* Social Login Section */}
            <div className="login-modern-social-section">
              <div className="login-modern-social-content">
                {/* Google Identity Services button (rendered by SDK) */}
                <div id="gsi-button-container" style={{display: googleSdkLoaded ? 'block' : 'none'}} />

                {/* Fallback Continue with Google (manual token prompt) */}
                {!googleSdkLoaded && (
                  <button 
                    className="login-modern-social-btn login-modern-google-btn"
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={isGoogleLoading}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    {isGoogleLoading ? 'Đang đăng nhập với Google...' : 'Continue with Google'}
                  </button>
                )}

                {/* Continue with Facebook */}
                <button className="login-modern-social-btn login-modern-facebook-btn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2"/>
                  </svg>
                  Continue with Facebook
                </button>
                
                {/* Create New Account */}
                <button 
                  className="login-modern-social-btn login-modern-email-btn"
                  onClick={() => setIsSignUpVisible(true)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
                  </svg>
                  Create New Account
                </button>
              </div>
            </div>
          </div>

          {/* Footer Links */}
          <div className="login-modern-footer-links">
            <button 
              type="button"
              onClick={handleCantLogin} 
              className="login-modern-cant-login-link"
            >
              Can't log in?
            </button>
            <div className="login-modern-terms-text">
              <div>Secure Login with reCAPTCHA subject to Google</div>
              <div className="login-modern-terms-links-container">
                <a href="#" className="login-modern-terms-link">Terms</a>
                <span className="login-modern-terms-and">&</span>
                <a href="#" className="login-modern-terms-link">Privacy</a>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="login-success-modal-overlay">
          <div className="login-success-modal">
            <div className="login-success-icon">✅</div>
            <h3>Đăng nhập thành công!</h3>
            <p>Bạn sẽ được chuyển hướng đến trang chính.</p>
            <div className="login-success-progress">
              <div className="login-success-progress-bar"></div>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
}

